import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from 'components/ui/sheet';
import { QuestionPreview, type QuestionPreviewData } from 'components/questions';
import type { BankQuestion } from 'modules/questionBank/types';
import { StatusBadge } from 'components/ui/status-badge';
import { QUESTION_TYPE_META, DIFFICULTY_TONE } from './questionTypeMeta';

/** The board's plain tag: grey fill, no border. */
const TAG = 'inline-flex h-[22px] items-center rounded-md bg-muted px-2 text-[12px] font-medium text-foreground/80';

export interface QuestionPreviewPanelProps {
  question: BankQuestion;
  subjectName: string;
  canEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
  returnFocusRef: React.RefObject<HTMLElement>;
}

/** Modal preview shared by the desktop table and mobile cards. */
export const QuestionPreviewPanel: React.FC<QuestionPreviewPanelProps> = ({
  question,
  subjectName,
  canEdit,
  onEdit,
  onClose,
  returnFocusRef,
}) => {
  const { t } = useTranslation();
  const meta = QUESTION_TYPE_META[question.type];
  const typeLabel = meta?.labelKey ? t(meta.labelKey) : question.type;

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="right"
        closeLabel={t('close')}
        className="flex w-full max-w-none flex-col gap-0 overflow-hidden p-0 sm:max-w-[640px] lg:max-w-[720px] [&>button]:flex [&>button]:h-10 [&>button]:w-10 [&>button]:items-center [&>button]:justify-center [&>button]:rounded-md"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus();
        }}
      >
        <SheetHeader className="min-h-[72px] shrink-0 flex-row items-center gap-3 space-y-0 border-b border-border py-4 pl-4 pr-16 text-left sm:pl-6">
          <SheetTitle className="min-w-0 flex-1">{t('preview')}</SheetTitle>
          <SheetDescription className="sr-only">{typeLabel} · {subjectName}</SheetDescription>
          {canEdit && (
            <Button size="sm" className="h-10 shrink-0 px-4" onClick={onEdit}>
              {t('edit')}
            </Button>
          )}
        </SheetHeader>

        {/* Body */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain p-4 pb-[max(1rem,env(safe-area-inset-bottom))] scrollbar-thin sm:p-6">
          {/* Type, subject and points are plain tags; only difficulty carries a tone. */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={TAG}>{typeLabel}</span>
            <span className={TAG}>{subjectName}</span>
            {question.difficulty && (
              <StatusBadge tone={DIFFICULTY_TONE[question.difficulty] ?? 'neutral'} dot={false}>
                {t(question.difficulty)}
              </StatusBadge>
            )}
            <span className={`${TAG} tabular-nums`}>{t('n_points', { count: question.points })}</span>
          </div>

          {/* Stem, answer key, and explanation — every question type, rendered
              the way a candidate sees it. */}
          <QuestionPreview
            question={question as unknown as QuestionPreviewData}
            showExplanation
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default QuestionPreviewPanel;
